import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import {
  Check, Trash2, Star, Handshake, User, X, MessageSquareQuote,
  Clock, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUS_FILTERS = ["queue", "pending", "approved", "rejected", "all"];
const CATEGORY_FILTERS = ["all", "user", "partner"];

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL");
  } catch { return "—"; }
}

function Stars({ n }) {
  const value = Math.max(1, Math.min(5, Number(n) || 5));
  return (
    <span className="inline-flex" aria-label={`${value} / 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={12} strokeWidth={1.5}
              className={i < value ? "text-amber-500 fill-amber-500" : "text-neutral-300"} />
      ))}
    </span>
  );
}

/**
 * Compact per-row moderation card. The "queue" tab shows these in a
 * stacked column so admins never need to scroll horizontally — approve /
 * reject / delete each ship a short optional note that persists as
 * `review_note` on the doc.
 */
function QueueCard({ item, busy, onApprove, onReject, onDelete, t, lang }) {
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState(false);
  const cat = item.category || "user";

  const run = async (fn) => {
    await fn(item.id, note.trim());
    setNote("");
  };

  return (
    <div className="ed-card p-4" data-testid={`admin-ts-queue-card-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-serif text-lg leading-tight">{item.alias || t("admTsAnon")}</p>
            {cat === "partner" && (
              <span className="ed-badge inline-flex items-center gap-1 border-amber-600 text-amber-800 bg-amber-50">
                <Handshake size={9} strokeWidth={2} /> {t("admTsCatPartner")}
              </span>
            )}
            {cat === "user" && (
              <span className="ed-badge inline-flex items-center gap-1 border-indigo-500 text-indigo-800 bg-indigo-50">
                <User size={9} strokeWidth={2} /> {t("admTsCatUser")}
              </span>
            )}
            {item.lang && (
              <span className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 border border-neutral-300 px-1.5 py-0.5">
                {item.lang}
              </span>
            )}
            <Stars n={item.rating} />
          </div>
          <p className="font-mono text-[0.65rem] text-neutral-500 mt-1">
            {[item.role, item.city].filter(Boolean).join(" · ") || item.email}
            {" · "}
            <span title={item.created_at}>{fmtDate(item.created_at, lang)}</span>
          </p>
        </div>
        <div className="text-right shrink-0">
          <span className={`ed-badge ${item.status === "approved" ? "badge-invited" : item.status === "rejected" ? "badge-rejected" : "badge-applied"}`}>
            {t(`admTsStatus${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`)}
          </span>
        </div>
      </div>

      <p className="mt-3 text-sm font-serif italic leading-snug whitespace-pre-wrap border-l-2 border-neutral-200 pl-3">
        "{item.body}"
      </p>

      {item.review_note && (
        <p className="mt-3 text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 px-3 py-2">
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mr-2">
            {t("admTsNoteLabel")}
          </span>
          {item.review_note}
        </p>
      )}

      <div className="mt-4 flex items-start gap-2 flex-wrap">
        <input
          data-testid={`admin-ts-note-${item.id}`}
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 200))}
          placeholder={t("admTsNotePlaceholder")}
          className="flex-1 min-w-[220px] px-3 py-1.5 border border-neutral-300 focus:border-neutral-900 outline-none text-sm font-mono text-neutral-800 placeholder:text-neutral-400"
        />
        {item.status !== "approved" && (
          <button
            data-testid={`admin-ts-approve-${item.id}`}
            disabled={busy === item.id}
            onClick={() => run(onApprove)}
            className="px-3 py-1.5 border border-emerald-500 text-emerald-800 bg-emerald-50 font-mono text-[0.6rem] uppercase tracking-wider hover:bg-emerald-100 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
          >
            <Check size={12} strokeWidth={2} /> {t("admTsApprove")}
          </button>
        )}
        {item.status !== "rejected" && (
          <button
            data-testid={`admin-ts-reject-${item.id}`}
            disabled={busy === item.id}
            onClick={() => run(onReject)}
            className="px-3 py-1.5 border border-rose-500 text-rose-800 bg-rose-50 font-mono text-[0.6rem] uppercase tracking-wider hover:bg-rose-100 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
          >
            <X size={12} strokeWidth={2} /> {t("admTsReject")}
          </button>
        )}
        <button
          data-testid={`admin-ts-delete-${item.id}`}
          disabled={busy === item.id}
          onClick={() => onDelete(item.id)}
          className="px-3 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-rose-700 hover:text-rose-700 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
          title={t("admTsDeleteHint")}
        >
          <Trash2 size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

export default function TestimonialsAdmin({ onChanged }) {
  const { t, lang } = useT();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("queue");
  const [catFilter, setCatFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/testimonials");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error(t("admActionFailed"));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const withAction = async (fn, successKey) => {
    try {
      await fn();
      toast.success(t(successKey));
      await load();
      onChanged?.();
    } catch {
      toast.error(t("admActionFailed"));
    } finally { setBusy(null); }
  };

  const approve = (id, note) => {
    setBusy(id);
    return withAction(() => api.post(`/admin/testimonials/${id}/approve`, { note: note || "" }), "admTsApproved");
  };
  const reject = (id, note) => {
    setBusy(id);
    return withAction(() => api.post(`/admin/testimonials/${id}/reject`, { note: note || "" }), "admTsRejected");
  };
  const del = (id) => {
    if (!confirm(t("admTsDeleteConfirm"))) return;
    setBusy(id);
    return withAction(() => api.delete(`/admin/testimonials/${id}`), "admTsDeleted");
  };

  const itemCat = (it) => (it.category || "user");

  // The "queue" tab is a smart pending-first view: pending items on top,
  // then most recent rejections so the admin sees repeat submitters.
  const filtered = useMemo(() => {
    let list = items || [];
    if (filter === "queue") {
      list = list.filter((it) => it.status === "pending");
    } else if (filter !== "all") {
      list = list.filter((it) => it.status === filter);
    }
    if (catFilter !== "all") list = list.filter((it) => itemCat(it) === catFilter);
    return list;
  }, [items, filter, catFilter]);

  const queueCount = (items || []).filter((it) => it.status === "pending").length;
  const catCounts = (items || [])
    .filter((it) => filter === "queue" ? it.status === "pending" : (filter === "all" || it.status === filter))
    .reduce((acc, it) => {
      const c = itemCat(it);
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});

  const useQueueView = filter === "queue";

  return (
    <div data-testid="admin-testimonials-section">
      <div className="flex items-start gap-3 mb-2">
        <MessageSquareQuote size={16} strokeWidth={1.5} className="text-neutral-500 mt-1" />
        <div>
          <p className="ed-eyebrow">{t("admTsTitle")}</p>
          <p className="text-sm font-serif italic text-neutral-600 mt-1 max-w-2xl">
            {t("admTsIntro")}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5 mt-4">
        <div className="flex border border-neutral-300 w-fit" data-testid="admin-ts-status-filters">
          {STATUS_FILTERS.map((s) => {
            const isQueue = s === "queue";
            const active = filter === s;
            return (
              <button
                key={s}
                data-testid={`admin-ts-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors inline-flex items-center gap-1.5 ${
                  active ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-100"
                }`}
              >
                {isQueue && <Clock size={11} strokeWidth={1.5} />}
                {t(`admTsFilter${s.charAt(0).toUpperCase() + s.slice(1)}`)}
                {isQueue && queueCount > 0 && (
                  <span className={`ml-1 tabular-nums ${active ? "text-amber-300" : "text-amber-700"}`}>
                    {queueCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex border border-neutral-300 w-fit" data-testid="admin-ts-category-filters">
          {CATEGORY_FILTERS.map((c) => {
            const count = c === "all" ? undefined : (catCounts[c] || 0);
            return (
              <button
                key={c}
                data-testid={`admin-ts-cat-${c}`}
                onClick={() => setCatFilter(c)}
                className={`px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors inline-flex items-center gap-1.5 ${
                  catFilter === c ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-100"
                }`}
              >
                {c === "partner" && <Handshake size={11} strokeWidth={1.5} />}
                {c === "user" && <User size={11} strokeWidth={1.5} />}
                {t(`admTsCat${c.charAt(0).toUpperCase() + c.slice(1)}`)}
                {count !== undefined && (
                  <span className={`ml-1 tabular-nums ${catFilter === c ? "text-neutral-400" : "text-neutral-500"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {items === null ? (
        <p className="ed-eyebrow">{t("loading")}</p>
      ) : filtered.length === 0 ? (
        <div className="ed-card p-10 text-center" data-testid="admin-ts-empty">
          <p className="font-serif italic text-neutral-500 text-lg">
            {useQueueView ? t("admTsQueueEmpty") : t("admTsEmpty")}
          </p>
          {useQueueView && (
            <p className="text-xs font-mono text-neutral-400 uppercase tracking-[0.15em] mt-3">
              {t("admTsQueueAllClear")}
            </p>
          )}
        </div>
      ) : useQueueView ? (
        // Queue view — stacked cards with big action buttons + note.
        <div className="space-y-3">
          {filtered.map((it) => (
            <QueueCard
              key={it.id} item={it} busy={busy}
              onApprove={approve} onReject={reject} onDelete={del}
              t={t} lang={lang}
            />
          ))}
        </div>
      ) : (
        // Non-queue views — compact table for scanning history.
        <div className="ed-card overflow-x-auto">
          <table className="ed-table">
            <thead>
              <tr>
                <th>{t("admTsColAuthor")}</th>
                <th>{t("admTsColQuote")}</th>
                <th>{t("admTsColRating")}</th>
                <th>{t("admTsColSubmitted")}</th>
                <th>{t("admTsColStatus")}</th>
                <th className="text-right">{t("admTsColActions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it, i) => (
                <tr key={it.id} data-testid={`admin-ts-row-${i}`}>
                  <td>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-serif text-base leading-tight">{it.alias || "—"}</p>
                      {itemCat(it) === "partner" && (
                        <span className="ed-badge inline-flex items-center gap-1 border-amber-600 text-amber-800 bg-amber-50">
                          <Handshake size={9} strokeWidth={2} /> {t("admTsCatPartner")}
                        </span>
                      )}
                      {it.lang && (
                        <span className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 border border-neutral-300 px-1 py-0.5">
                          {it.lang}
                        </span>
                      )}
                    </div>
                    <p className="font-mono text-[0.65rem] text-neutral-500">
                      {[it.role, it.city].filter(Boolean).join(" · ") || it.email}
                    </p>
                  </td>
                  <td className="max-w-md">
                    <p className="text-sm font-serif italic leading-snug whitespace-pre-wrap">"{it.body}"</p>
                    {it.review_note && (
                      <p className="text-[0.65rem] font-mono text-neutral-500 mt-1">
                        {t("admTsNoteLabel")}: {it.review_note}
                      </p>
                    )}
                  </td>
                  <td><Stars n={it.rating} /></td>
                  <td className="font-mono text-xs">{fmtDate(it.created_at, lang)}</td>
                  <td>
                    <span className={`ed-badge ${it.status === "approved" ? "badge-invited" : it.status === "rejected" ? "badge-rejected" : "badge-applied"}`}>
                      {t(`admTsStatus${it.status.charAt(0).toUpperCase() + it.status.slice(1)}`)}
                    </span>
                  </td>
                  <td>
                    <div className="flex justify-end flex-wrap gap-1.5">
                      {it.status !== "approved" && (
                        <button
                          data-testid={`admin-ts-approve-${i}`}
                          disabled={busy === it.id}
                          onClick={() => approve(it.id, "")}
                          className="px-2.5 py-1.5 border border-emerald-500 text-emerald-800 bg-emerald-50 font-mono text-[0.6rem] uppercase tracking-wider hover:bg-emerald-100 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                        >
                          <Check size={11} strokeWidth={2} /> {t("admTsApprove")}
                        </button>
                      )}
                      {it.status !== "rejected" && (
                        <button
                          data-testid={`admin-ts-reject-${i}`}
                          disabled={busy === it.id}
                          onClick={() => reject(it.id, "")}
                          className="px-2.5 py-1.5 border border-rose-500 text-rose-800 bg-rose-50 font-mono text-[0.6rem] uppercase tracking-wider hover:bg-rose-100 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                        >
                          <X size={11} strokeWidth={2} /> {t("admTsReject")}
                        </button>
                      )}
                      <button
                        data-testid={`admin-ts-delete-${i}`}
                        disabled={busy === it.id}
                        onClick={() => del(it.id)}
                        className="px-2.5 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-rose-700 hover:text-rose-700 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        <Trash2 size={11} strokeWidth={1.5} /> {t("admTsDelete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
